import { ChevronDown } from 'lucide-react';
import React from 'react';
import { Select as AriaSelect, Button as AriaButton, SelectValue, ListBox } from 'react-aria-components';
import { cn, focusRing } from '../../../components/utils';
import type { BaseFieldProps } from '../../../components/types';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { FieldDescription } from '../base-controls/FieldDescription';
import { FieldError } from '../base-controls/FieldError';
import { DropdownItem, DropdownSection } from '../base-controls/ListBox';
import { Popover } from '../../../components/Popover';
import { tv } from 'tailwind-variants';
import { baseControlStyles } from '../base-controls/util';

const selectStyles = tv({
	extend: baseControlStyles,
	slots: {
		base: 'sm:max-w-80',
		trigger:
			'group inline-flex max-h-[42px] w-full cursor-default items-center justify-center gap-4 overflow-hidden rounded-md border border-gray-300 bg-white py-2.5 pl-3 pr-2 text-start font-normal text-gray-800 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)] transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-200',
		selectValue: 'min-w-0 flex-1 truncate text-sm data-[placeholder]:italic data-[placeholder]:text-gray-400'
	},
	variants: {
		density: {
			default: {},
			compact: {
				trigger: 'max-h-[32px] py-1.5 pl-2 pr-1 text-sm'
			}
		},
		isInvalid: {
			true: {
				trigger: 'border-red-600'
			}
		}
	}
});

export interface SelectFieldItem {
	id: string;
	name: string;
	description?: string;
	className?: string;
}

export interface SelectFieldLabelItem {
	id: number;
	label: string;
	description?: string;
	className?: string;
}

export interface SelectFieldSection {
	id: string;
	title: string;
	items: SelectFieldItem[];
}

export interface SelectFieldProps extends BaseFieldProps {
	placeholder?: string;
	noOptionsMessage?: string;
	value?: string | number | null;
	items?: SelectFieldItem[] | SelectFieldLabelItem[];
	sections?: SelectFieldSection[];
	children?: React.ReactNode;
	onChange?: (value: string | number | null) => void;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	isInvalid?: boolean;
	name?: string;
	containerClassName?: string;
	className?: string;
	density?: 'default' | 'compact';
	orientation?: 'horizontal' | 'vertical';
}

function hasLabels(x: SelectFieldItem[] | SelectFieldLabelItem[]): x is SelectFieldLabelItem[] {
	return x.length > 0 && 'label' in x[0];
}

export function SelectField({
	label,
	description,
	placeholder,
	noOptionsMessage,
	value,
	items = [],
	sections,
	children,
	onChange,
	isDisabled,
	isReadOnly,
	isInvalid,
	name,
	containerClassName,
	className,
	density = 'default',
	orientation = 'vertical',
	hideLabel,
	required,
	error
}: SelectFieldProps) {
	const { base, trigger, selectValue } = selectStyles({
		density,
		isInvalid
	});

	const selectItems: SelectFieldItem[] = hasLabels(items)
		? items.map(x => ({ id: x.id.toString(), name: x.label, description: x.description, className: x.className }))
		: items;
	const hasOptions = sections ? sections.some(x => x.items.length > 0) : selectItems.length > 0;

	const renderItem = (item: SelectFieldItem) => (
		<DropdownItem id={item.id} textValue={item.name} description={item.description} className={item.className}>
			<span dangerouslySetInnerHTML={{ __html: item.name }} />
		</DropdownItem>
	);

	return (
		<AriaSelect
			value={value?.toString() ?? null}
			onChange={key => onChange?.(key as string | null)}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			aria-label={label}
			name={name}
			className={cn(base(), containerClassName, orientation === 'horizontal' && 'flex-row items-center gap-2')}
		>
			{!hideLabel && label ? (
				<FieldLabelWithHtml
					className={orientation === 'horizontal' ? 'mr-1 text-nowrap' : 'mb-1'}
					label={label}
					required={required ?? false}
				/>
			) : null}
			<AriaButton
				type="button"
				isDisabled={isDisabled || isReadOnly}
				className={renderProps => cn(focusRing(renderProps), trigger(), className)}
			>
				<SelectValue className={selectValue()}>
					{({ selectedText }) =>
						selectedText ? (
							<span className="block truncate" dangerouslySetInnerHTML={{ __html: selectedText }} />
						) : hasOptions ? (
							placeholder ?? 'Select an option'
						) : (
							noOptionsMessage ?? 'No options found'
						)
					}
				</SelectValue>
				<ChevronDown aria-hidden className="size-4 shrink-0 text-gray-600 group-disabled:text-gray-400" />
			</AriaButton>
			{description && <FieldDescription>{description}</FieldDescription>}
			{children}
			{error && <FieldError {...error} />}
			<Popover className="z-50 min-w-[--trigger-width]">
				{sections ? (
					<ListBox items={sections} className="max-h-[300px] overflow-auto p-1 outline-none">
						{section => (
							<DropdownSection id={section.id} title={section.title} items={section.items}>
								{renderItem}
							</DropdownSection>
						)}
					</ListBox>
				) : (
					<ListBox items={selectItems} className="max-h-[300px] overflow-auto p-1 outline-none">
						{renderItem}
					</ListBox>
				)}
			</Popover>
		</AriaSelect>
	);
}
